import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  CircularProgress,
  Alert,
  IconButton,
  Stack,
  Button,
  Select,
  MenuItem,
  Collapse,
  Tooltip,
} from "@mui/material";
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  Refresh,
  History as HistoryIcon,
  Visibility,
} from "@mui/icons-material";
import {
  fetchAuditLogsForEntity,
  selectAuditLogs,
  selectAuditLogLoading,
  selectAuditLogError,
  selectAuditLogPagination,
  clearError,
} from "../../../store/slices/auditLogSlice";
import {
  fetchFeedbackById,
  updateFeedbackStatus,
} from "../../../store/slices/adminFeedbackSlice";
import FeedbackDetailModal from "../FeedbackDetailModal";
import AuditLogChangesTable from "../audit/AuditLogChangesTable.jsx";

const ChitalishteAuditHistory = ({ chitalishteId, chitalishteName }) => {
  const dispatch = useDispatch();
  const auditLogs = useSelector(selectAuditLogs);
  const loading = useSelector(selectAuditLogLoading);
  const error = useSelector(selectAuditLogError);
  const pagination = useSelector(selectAuditLogPagination);

  const [expandedRows, setExpandedRows] = useState({});
  const [filterType, setFilterType] = useState("all");
  const [selectedFeedback, setSelectedFeedback] = useState(null);
  const [feedbackModalOpen, setFeedbackModalOpen] = useState(false);
  const [loadingFeedback, setLoadingFeedback] = useState(false);

  useEffect(() => {
    if (chitalishteId) {
      dispatch(
        fetchAuditLogsForEntity({
          entityId: chitalishteId,
          entityType: "CHITALISHTE",
          page: pagination.page,
          size: pagination.size,
        }),
      );
    }
  }, [dispatch, chitalishteId, pagination.page, pagination.size]);

  const handleRefresh = () => {
    if (chitalishteId) {
      dispatch(
        fetchAuditLogsForEntity({
          entityId: chitalishteId,
          entityType: "CHITALISHTE",
          page: 0,
          size: pagination.size,
        }),
      );
    }
  };

  const handleRowsPerPageChange = (event) => {
    const newSize = parseInt(event.target.value, 10);
    if (chitalishteId) {
      dispatch(
        fetchAuditLogsForEntity({
          entityId: chitalishteId,
          entityType: "CHITALISHTE",
          page: 0,
          size: newSize,
        }),
      );
    }
  };

  const toggleRow = (logId) => {
    setExpandedRows((prev) => ({
      ...prev,
      [logId]: !prev[logId],
    }));
  };

  const handleViewFeedback = async (feedbackId) => {
    setLoadingFeedback(true);
    try {
      const response = await dispatch(fetchFeedbackById(feedbackId)).unwrap();
      setSelectedFeedback(response);
      setFeedbackModalOpen(true);
    } catch (error) {
      console.error("Error loading feedback:", error);
    } finally {
      setLoadingFeedback(false);
    }
  };

  const getActionText = (actionType) => {
    switch (actionType?.toUpperCase()) {
      case "CREATE":
        return "Създаване";
      case "UPDATE":
        return "Редактиране";
      case "DELETE":
        return "Изтриване";
      case "READ":
        return "Преглед";
      default:
        return actionType || "Неизвестно";
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return "Неизвестна дата";
    try {
      const date = new Date(dateString);
      return date.toLocaleString("bg-BG", {
        day: "numeric",
        month: "numeric",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch (error) {
      return dateString;
    }
  };

  const prepareDetailedChanges = (log) => {
    if (log.details) {
         try {
            const details = JSON.parse(log.details);
            if (details.changedFields && typeof details.changedFields === 'object' && Object.keys(details.changedFields).length > 0) {
                return details.changedFields;
            }
        } catch (e) {
            console.error("Error parsing log.details:", e);
        }
    }

    if (log.oldValues && log.newValues && log.changedFields) {
      try {
        const oldVals = JSON.parse(log.oldValues);
        const newVals = JSON.parse(log.newValues);
        const fields = typeof log.changedFields === 'string'
          ? log.changedFields.split(',').map(f => f.trim())
          : Object.keys(log.changedFields);

        const changes = {};
        for (const field of fields) {
          changes[field] = {
            oldValue: oldVals[field],
            newValue: newVals[field],
          };
        }
        if (Object.keys(changes).length > 0) return changes;
      } catch (e) {
        console.error("Error processing old/new values:", e);
      }
    }
    
    return null;
  }

  const renderChangedFields = (log) => {
    const changes = prepareDetailedChanges(log);

    if (!changes) {
      return null;
    }

    if (typeof changes === 'object') {
      return <AuditLogChangesTable changedFields={changes} />;
    }
    
    return null;
  };

  const filteredLogs = auditLogs.filter((log) => {
    if (filterType === "all") return true;
    if (filterType === "feedback") return log.feedbackId != null;
    return false;
  });

  if (!chitalishteId) {
    return (
      <Alert severity="warning">
        Не е избрано читалище за преглед на историята
      </Alert>
    );
  }

  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Stack direction="row" spacing={1} alignItems="center">
          <HistoryIcon sx={{ color: "#FF8C42", fontSize: 28 }} />
          <Typography variant="h6" sx={{ fontWeight: 600 }}>
            История на промените
          </Typography>
        </Stack>
        <Button
          startIcon={<Refresh />}
          onClick={handleRefresh}
          variant="outlined"
          disabled={loading}
          sx={{
            textTransform: "none",
            borderRadius: "8px",
            borderColor: "#1976d2",
            color: "#1976d2",
            "&:hover": {
              borderColor: "#1565c0",
              bgcolor: "rgba(25, 118, 210, 0.04)",
            },
          }}
        >
          Обнови
        </Button>
      </Box>

      <Typography
        variant="body2"
        color="text.secondary"
        sx={{ mb: 3, fontWeight: 500 }}
      >
        {chitalishteName}
      </Typography>

      {error && (
        <Alert
          severity="error"
          sx={{ mb: 3, borderRadius: "8px" }}
          onClose={() => dispatch(clearError())}
        >
          {error}
        </Alert>
      )}

      <Box
        sx={{
          mb: 3,
          display: "flex",
          justifyContent: "flex-end",
        }}
      >
        <Stack
          direction="row"
          sx={{
            bgcolor: "#F5F5F5",
            borderRadius: "12px",
            p: 0.5,
            display: "inline-flex",
          }}
        >
          <Button
            onClick={() => setFilterType("all")}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 1,
              bgcolor: filterType === "all" ? "#FF8C42" : "transparent",
              color: filterType === "all" ? "white" : "text.secondary",
              fontWeight: 600,
              fontSize: "0.875rem",
              minWidth: "100px",
              boxShadow:
                filterType === "all"
                  ? "0 2px 8px rgba(255, 140, 66, 0.25)"
                  : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor:
                  filterType === "all" ? "#E67A35" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            Всички
          </Button>
          <Button
            onClick={() => setFilterType("feedback")}
            sx={{
              textTransform: "none",
              borderRadius: "10px",
              px: 3,
              py: 1,
              bgcolor: filterType === "feedback" ? "#FF8C42" : "transparent",
              color: filterType === "feedback" ? "white" : "text.secondary",
              fontWeight: 600,
              fontSize: "0.875rem",
              minWidth: "120px",
              boxShadow:
                filterType === "feedback"
                  ? "0 2px 8px rgba(255, 140, 66, 0.25)"
                  : "none",
              transition: "all 0.2s ease-in-out",
              "&:hover": {
                bgcolor:
                  filterType === "feedback" ? "#E67A35" : "rgba(0, 0, 0, 0.04)",
              },
            }}
          >
            От сигнал
          </Button>
        </Stack>
      </Box>

      {loading && (
        <Box sx={{ display: "flex", justifyContent: "center", p: 6 }}>
          <CircularProgress sx={{ color: "#FF8C42" }} />
        </Box>
      )}

      {!loading && filteredLogs.length > 0 && (
        <>
          <TableContainer
            component={Paper}
            sx={{
              boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
              borderRadius: "12px",
              overflow: "hidden",
            }}
          >
            <Table>
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: "#FAFAFA",
                    "& th": {
                      borderBottom: "2px solid #E0E0E0",
                    },
                  }}
                >
                  <TableCell sx={{ width: "50px", py: 2 }}></TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, py: 2, fontSize: "0.875rem" }}
                  >
                    Действие
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, py: 2, fontSize: "0.875rem" }}
                  >
                    Обект
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, py: 2, fontSize: "0.875rem" }}
                  >
                    Изпълнено от
                  </TableCell>
                  <TableCell
                    sx={{ fontWeight: 700, py: 2, fontSize: "0.875rem" }}
                  >
                    Дата
                  </TableCell>
                  <TableCell
                    sx={{
                      fontWeight: 700,
                      py: 2,
                      fontSize: "0.875rem",
                      width: "200px",
                    }}
                  >
                    От сигнал
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredLogs.map((log) => (
                  <React.Fragment key={log.id}>
                    <TableRow
                      hover
                      sx={{
                        "&:hover": { bgcolor: "#FAFAFA" },
                        transition: "background-color 0.2s",
                      }}
                    >
                      <TableCell sx={{ py: 2.5 }}>
                        <IconButton
                          size="small"
                          onClick={() => toggleRow(log.id)}
                          sx={{
                            color: "text.secondary",
                            transition: "transform 0.2s",
                            transform: expandedRows[log.id]
                              ? "rotate(0deg)"
                              : "rotate(0deg)",
                          }}
                        >
                          {expandedRows[log.id] ? (
                            <KeyboardArrowDown />
                          ) : (
                            <KeyboardArrowRight />
                          )}
                        </IconButton>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Chip
                          label={getActionText(log.action)}
                          size="small"
                          sx={{
                            bgcolor: "#E3F2FD",
                            color: "#1976D2",
                            fontWeight: 600,
                            borderRadius: "8px",
                            fontSize: "0.75rem",
                          }}
                        />
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body2" sx={{ fontWeight: 500 }}>
                          Читалище
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {log.performedBy || "admin"}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        <Typography variant="body2" color="text.secondary">
                          {formatDate(log.performedAt)}
                        </Typography>
                      </TableCell>
                      <TableCell sx={{ py: 2.5 }}>
                        {log.feedbackId ? (
                          <Stack
                            direction="row"
                            spacing={1}
                            alignItems="center"
                          >
                            <Chip
                              label="Да"
                              size="small"
                              sx={{
                                bgcolor: "#FFF3E0",
                                color: "#FF8C42",
                                fontWeight: 600,
                                borderRadius: "8px",
                                fontSize: "0.75rem",
                              }}
                            />
                            <Tooltip title="Преглед на сигнала">
                              <IconButton
                                size="small"
                                onClick={() =>
                                  handleViewFeedback(log.feedbackId)
                                }
                                disabled={loadingFeedback}
                                sx={{
                                  color: "#FF8C42",
                                  bgcolor: "rgba(255, 140, 66, 0.08)",
                                  "&:hover": {
                                    bgcolor: "rgba(255, 140, 66, 0.16)",
                                  },
                                  transition: "all 0.2s",
                                }}
                              >
                                {loadingFeedback ? (
                                  <CircularProgress
                                    size={16}
                                    sx={{ color: "#FF8C42" }}
                                  />
                                ) : (
                                  <Visibility fontSize="small" />
                                )}
                              </IconButton>
                            </Tooltip>
                          </Stack>
                        ) : (
                          <Chip
                            label="Не"
                            size="small"
                            sx={{
                              bgcolor: "#F5F5F5",
                              color: "#9E9E9E",
                              fontWeight: 600,
                              borderRadius: "8px",
                              fontSize: "0.75rem",
                            }}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                    <TableRow>
                      <TableCell
                        sx={{
                          py: 0,
                          borderBottom: expandedRows[log.id] ? 1 : 0,
                          borderColor: "#E0E0E0",
                        }}
                        colSpan={6}
                      >
                        <Collapse
                          in={expandedRows[log.id]}
                          timeout="auto"
                          unmountOnExit
                        >
                          <Box
                            sx={{
                              py: 3,
                              px: 3,
                              bgcolor: "#FAFAFA",
                              borderRadius: "8px",
                              m: 1,
                            }}
                          >
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: 700,
                                mb: 1.5,
                                color: "#424242",
                              }}
                            >
                              Описание:
                            </Typography>
                            <Typography
                              variant="body2"
                              color="text.secondary"
                              sx={{ mb: 3, lineHeight: 1.6 }}
                            >
                              {log.description || "Няма описание"}
                            </Typography>
                            {renderChangedFields(log)}
                            {log.feedbackId && (
                              <Box sx={{ mt: 3 }}>
                                <Typography
                                  variant="body2"
                                  sx={{
                                    fontWeight: 700,
                                    mb: 1.5,
                                    color: "#424242",
                                  }}
                                >
                                  Свързан сигнал:
                                </Typography>
                                <Stack
                                  direction="row"
                                  spacing={1}
                                  alignItems="center"
                                >
                                  <Chip
                                    label={`Сигнал #${log.feedbackId}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      borderColor: "#FF8C42",
                                      color: "#FF8C42",
                                      fontWeight: 600,
                                    }}
                                  />
                                  <Button
                                    size="small"
                                    startIcon={<Visibility />}
                                    onClick={() =>
                                      handleViewFeedback(log.feedbackId)
                                    }
                                    disabled={loadingFeedback}
                                    sx={{
                                      textTransform: "none",
                                      color: "#FF8C42",
                                      fontWeight: 600,
                                      "&:hover": {
                                        bgcolor: "rgba(255, 140, 66, 0.08)",
                                      },
                                    }}
                                  >
                                    Преглед на сигнала
                                  </Button>
                                </Stack>
                              </Box>
                            )}
                          </Box>
                        </Collapse>
                      </TableCell>
                    </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>

          <Box
            sx={{
              display: "flex",
              justifyContent: "flex-end",
              alignItems: "center",
              mt: 3,
              gap: 2,
            }}
          >
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              Редове на страница:
            </Typography>
            <Select
              value={pagination.size}
              onChange={handleRowsPerPageChange}
              size="small"
              sx={{
                minWidth: 70,
                borderRadius: "8px",
                "& .MuiOutlinedInput-notchedOutline": {
                  borderColor: "#E0E0E0",
                },
              }}
            >
              <MenuItem value={10}>10</MenuItem>
              <MenuItem value={20}>20</MenuItem>
              <MenuItem value={50}>50</MenuItem>
              <MenuItem value={100}>100</MenuItem>
            </Select>
            <Typography variant="body2" color="text.secondary" fontWeight={500}>
              {pagination.page * pagination.size + 1}-
              {Math.min(
                (pagination.page + 1) * pagination.size,
                pagination.totalElements,
              )}{" "}
              от {pagination.totalElements}
            </Typography>
          </Box>
        </>
      )}

      {!loading && filteredLogs.length === 0 && (
        <Paper
          sx={{
            p: 6,
            textAlign: "center",
            borderRadius: "12px",
            boxShadow: "0 2px 8px rgba(0,0,0,0.08)",
          }}
        >
          <HistoryIcon
            sx={{ fontSize: 72, color: "text.secondary", mb: 2, opacity: 0.5 }}
          />
          <Typography
            variant="h6"
            color="text.secondary"
            gutterBottom
            fontWeight={600}
          >
            Няма намерени записи в историята
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {filterType === "feedback"
              ? "Няма промени от сигнали за това читалище"
              : "Все още няма регистрирани промени за това читалище"}
          </Typography>
        </Paper>
      )}

      {selectedFeedback && (
        <FeedbackDetailModal
          open={feedbackModalOpen}
          onClose={() => {
            setFeedbackModalOpen(false);
            setSelectedFeedback(null);
          }}
          feedback={selectedFeedback}
          onStatusUpdate={async (id, status, notes) => {
            await dispatch(
              updateFeedbackStatus({
                id,
                status,
                processedBy: "admin",
                notes,
              }),
            ).unwrap();
          }}
          onSuccess={() => {
            handleRefresh();
          }}
          chitalishte={null}
        />
      )}
    </Box>
  );
};

export default ChitalishteAuditHistory;
